import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaPlayCircle,
  FaGamepad,
  FaTrashAlt,
  FaUndo,
  FaUsers,
  FaStopCircle,
  FaSkull
} from 'react-icons/fa';
import * as Motion from 'motion/react';
import { NIL as uuidNIL } from 'uuid';
import { FaHeart } from 'react-icons/fa';
import { MdEmojiEvents } from 'react-icons/md';
import ConnectionState from '../../components/ConnectionState';
import DynamicLeagueEventUI from '../../components/DynamicLeagueEventUI';
import Loader from '../../components/Loaders';
import { UserAvatar } from '../../components/UserProfileItem';
import useAuth from '../../hooks/useAuth';
import useDialogBox from '../../hooks/useDialogBox';
import useSocket from '../../hooks/useSocket';
import { deleteLeague } from '../../api/v1/leagues';
import { animationStaggeredItem } from '../../animations';
import './styles.scss';

const { motion } = Motion;


const League = () => {
  const dynamicUIRef = useRef(null);
  const [league, setLeague] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLeagueEventOpen, setIsLeagueEventOpen] = useState(false);
  const { resetDialogBox, setDialogBox } = useDialogBox();
  const navigate = useNavigate();
  const { leagueId } = useParams();
  const { isConnected, send, subscribe } = useSocket();

  const { user: LeagueConductor } = useAuth();

  const confirmDeleteLeague = async (id) => {
    setDialogBox({
      title: 'Delete League',
      content: <p>Do you want to delete league <b>{league?.displayName || leagueId}</b>?</p>,
      actions: [
        { title: 'Delete League', type: 'danger', action: async () => await handleDeleteLeague(id) },
        { title: 'Cancel', type: 'cancel', action: () => resetDialogBox() }
      ]
    });
  };

  const handleDeleteLeague = async (leagueId) => {
    await deleteLeague(leagueId);
    resetDialogBox();

    navigate(-1);
  };

  const toggleSelectedLeagueUser = (userId) => {
    if (isLeagueEventOpen) {
      const leagueUpdate = { ...league };

      const userToUpdate = leagueUpdate.users.find((user) => user.id === userId);

      userToUpdate.isParticipant = !userToUpdate?.isParticipant;
      setLeague(leagueUpdate);

      send(
        JSON.stringify({
          channel: 'leagueEventUpdateMembers',
          payload: {
            leagueId: leagueId,
            ...leagueUpdate,
          }
        })
      );
    }
  };

  const toggleLeagueEventState = () => {
    setIsLeagueEventOpen(!isLeagueEventOpen);

    send(
      JSON.stringify({
        channel: 'toggleLeagueEventState',
        payload: {
          leagueConductorUserId: LeagueConductor.id,
          leagueId: leagueId,
          isLeagueEventOpen: !isLeagueEventOpen
        }
      })
    );
  }

  useEffect(() => {
    if (leagueId && isConnected) {
      const unsubscribe = subscribe(`leagueEvent-${leagueId}-response`, (message) => {
        setIsLeagueEventOpen(message?.payload.isLeagueEventOpen);

        setLeague(message?.payload);
      });

      send(JSON.stringify({
        channel: 'leagueEventRequest', payload: {
          leagueId: leagueId,
        }
      }));

      return unsubscribe;
    }
  }, [leagueId, isConnected]);

  useEffect(() => {
    (async () => {
      if (league) {
        setIsLoading(false);
      }
    })();
  }, [league]);

  return (
    <>
      <Motion.AnimatePresence>
        {isLoading && <Loader/>}
      </Motion.AnimatePresence>

      <div className="screen-navigation">
        <button className="screen-back-button" onClick={() => navigate(-1)}>&larr; Go Back</button>
      </div>

      <motion.div variants={animationStaggeredItem} className="screen-header">
        <div className="screen-title-wrapper">
          <div>
            <h2 className="screen-title">
              {league?.leagueDetails?.displayName}
            </h2>

            <p className="screen-subtitle">
              <b>League Slug:</b> {league?.leagueDetails?.leagueSlug}
            </p>

            <p className="screen-subtitle">
              <b>Conductor ID:</b> {league?.leagueDetails?.id}
            </p>
          </div>
        </div>

        {
          leagueId !== uuidNIL &&
          <button className="screen-header-button danger" onClick={() => confirmDeleteLeague(leagueId)}>
            <FaTrashAlt/>Delete League
          </button>
        }
      </motion.div>

      <motion.div variants={animationStaggeredItem} className="screen-row">
        <div className="screen-section flex-1">
          <div className="section-heading-wrapper">
            <div className="section-heading-group-row no-justify">
              <div>
                <h3 className="section-heading">
                  <FaUsers/>
                  Users
                  ({league?.users?.reduce((sum, item) => (item?.isParticipant ? sum + 1 : sum), 0)} / {league?.users?.length})
                </h3>

                <p>Select users to include in the league event</p>
              </div>
            </div>
          </div>

          <div className="league-user-list-wrapper">
            {
              !league?.users?.length ?
                <p>No users in this league...</p> :
                league?.users?.map((user, key) => {
                  return (
                    <div
                      key={key}
                      className={[
                        `league-user-list-item ${user?.isParticipant ? 'selected' : 'unselected'}`,
                        `${isLeagueEventOpen ? '' : 'disabled'}`
                      ].join(' ')}
                      onClick={() => toggleSelectedLeagueUser(user?.id)}
                    >
                      <div className={'league-user-list-item-group'}>
                        <ConnectionState
                          isConnected={
                            league?.users?.find((leagueUser) => {
                                return user?.devices.find((device) => {
                                  if (leagueUser.id === user?.id) {
                                    return device.isConnected === true
                                  }
                                })
                              }
                            )}
                        />
                        <UserAvatar userName={user?.userName}/>
                        <p key={key}>{user?.userName}</p>
                      </div>

                      <div className="league-user-list-item-group-vertical">
                        <p>
                          Score: <b>{user?.score}</b>
                        </p>

                        <p>
                          {
                            user?.alive !== null && !user?.alive && (
                              <span className="skull-icon">
                                <FaSkull/>
                              </span>
                            )
                          }
                          <span className="heart-icon">
                            <FaHeart/>
                          </span>
                          <b>x{user?.lives}</b>
                        </p>
                      </div>
                    </div>
                  )
                })
            }
          </div>
        </div>

        <div className="screen-column flex-2">
          <div className="screen-row">

            <div className="screen-section">
              <div className="section-heading-wrapper">
                <div className="section-heading-group-row no-justify">
                  <h3 className="section-heading">
                    <MdEmojiEvents/>
                    League Event
                  </h3>
                </div>
              </div>

              <button
                className={`screen-header-button ${isLeagueEventOpen ? 'danger' : 'success'}`}
                onClick={() => toggleLeagueEventState()}
              >
                {
                  isLeagueEventOpen ?
                    <><FaStopCircle/> Close League Event</> :
                    <><FaPlayCircle/> Open League Event</>
                }
              </button>
            </div>
          </div>

          <div className="screen-section flex-2">
            <div className="section-heading-wrapper">
              <div className="section-heading-group-row no-justify">
                <h3 className="section-heading">
                  <FaGamepad/>
                  Game Controls
                  <button disabled={!isLeagueEventOpen}
                          className={`reload-ui-button ${!isLeagueEventOpen ? 'disabled' : ''}`}
                          onClick={() => dynamicUIRef?.current.requestUI()}><FaUndo/></button>
                </h3>
              </div>
            </div>

            <div className="game-controls-wrapper">
              <DynamicLeagueEventUI
                controlActive={true}
                deviceId={league?.leagueUIHostDeviceId}
                leagueId={leagueId}
                isDeviceConnected={true}
                ref={dynamicUIRef}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </>
  )
};


export default League;
